<template>
  <e-dialog v-if="serviceGroup" v-model="isOpened" @after-leave="cancel">
    <template v-if="name" #title>
      <div class="caption">{{ name[0].value }}</div>
    </template>

    <e-alert v-if="isOperationDisabled(serviceGroup?.operations.edit)" variant="info" thin class="mb-6">
      <div class="paragraph-s_h text-secondary">
        Изменение Групп услуг для тарифных планов после отправки на модерацию доступно через обращение в
        <router-link :to="{ name: RouteNames.Support }" class="link">Поддержку</router-link>
      </div>
    </e-alert>

    <template v-if="!isEditing">
      <property-item title="Наименование группы услуг*">
        <multilanguage-text :text="serviceGroup.name" />
      </property-item>

      <template v-if="serviceGroup.services.length">
        <div class="paragraph-l mt-6 font-600">Услуги</div>


        <div class="services-list">
          <e-chip v-for="chip in serviceGroup.services" :key="chip.cid" class="chip-item">
            <div class="service-item">
              <span>{{ chip.shortName }}</span>
              <img 
                v-if="chip.isFirst" 
                :src="firstServiceIcon"
                alt="Первая услуга"
                title="Первая услуга"
              />
              <img 
                v-if="chip.isLast" 
                :src="lastServiceIcon"
                alt="Последняя услуга"
                title="Последняя услуга"
              />
            </div>
          </e-chip>
        </div>
        
        <div class="paragraph-xs text-secondary mt-4 d-flex align-center">
          <div class="d-flex align-center ga-1">
            <img 
              :src="firstServiceIcon"
              alt="Первая услуга"
              title="Первая услуга"
            />
            <span>Первая оказываемая услуга</span>
          </div>
          <div class="d-flex align-center ga-1 ml-4">
            <img 
              :src="lastServiceIcon"
              alt="Последняя услуга"
              title="Последняя услуга"
            />
            <span>Последняя оказываемая услуга</span>
          </div>
        </div>
      </template>

      <property-item v-if="serviceGroup.description.length" title="Описание группы услуг (необязательно)" class="mt-6">
        <multilanguage-text :text="serviceGroup.description" />
      </property-item>

      <e-btn
        v-if="!isOperationHidden(serviceGroup?.operations.edit)"
        prepend-icon-src="editPen"
        :loading="isEditTariffScheduleServiceGroupStartLoading"
        variant="primary"
        class="ml-auto mt-6"
        :disabled="isOperationDisabled(serviceGroup?.operations.edit)"
        @click="startEdit"
      >
        Редактировать
      </e-btn>
    </template>

    <service-group-form
      v-else
      :tariff-schedule-cid="tariffScheduleCid"
      :hotel-set-cid="hotelSetCid"
      :service-group="serviceGroup"
      @success:update="onUpdateSuccess"
      @cancel="cancel"
    />
  </e-dialog>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import type { MultilanguageValue } from '@/core/utils/languages';
import PropertyItem from '@/core/components/interface/PropertyItem.vue';
import EDialog from '@/core/components/modals/Dialog.vue';
import { EAlert, EBtn, EChip, MultilanguageText } from '@/core/components/ui';
import { RouteNames } from '@/core/plugins/router/routeNames';
import { deepCloneObject } from '@/core/utils/object';
import { isOperationDisabled, isOperationHidden } from '@/core/utils/permissions';
import ServiceGroupForm from '@/parts/tariffs/components/ServiceGroupForm.vue';
import { type ServiceGroup, useServiceGroupsStore } from '@/parts/tariffs/store/serviceGroups';
import firstServiceIcon from '@/assets/img/pages/tariffs/service-groups/first-service.svg';
import lastServiceIcon from '@/assets/img/pages/tariffs/service-groups/last-service.svg';

const props = defineProps<{
  tariffScheduleCid?: string;
  serviceGroupCid: string;
  hotelSetCid: string;
}>();

const emit = defineEmits<{
  (event: 'loaded'): void;
  (event: 'success', data: ServiceGroup): void;
}>();

const {
  getTariffScheduleServiceGroup,
  getTariffServiceGroups,
  isEditTariffScheduleServiceGroupStartLoading,
  editTariffScheduleServiceGroupStart,
  editTariffScheduleServiceGroupCancel,
} = useServiceGroupsStore();

const isOpened = defineModel<boolean>();
const isEditing = ref(false);

const startEdit = async () => {
  const result = await editTariffScheduleServiceGroupStart(props.serviceGroupCid);

  if (result && !result.errors) {
    isEditing.value = true;
  }
};

const serviceGroup = ref<ServiceGroup>();

const name = ref<MultilanguageValue[]>();

const getServiceGroup = async () => {
  const result = await getTariffScheduleServiceGroup(props.serviceGroupCid, props.hotelSetCid, props.tariffScheduleCid);

  if (result?.data) {
    serviceGroup.value = result.data.tariffScheduleServiceGroup;

    if (serviceGroup.value) {
      name.value = deepCloneObject(serviceGroup.value.name);
    }
  }

  emit('loaded');
};

const cancel = async () => {
  await editTariffScheduleServiceGroupCancel();

  isEditing.value = false;
};

const onUpdateSuccess = async (data: ServiceGroup) => {
  if (data) {
    serviceGroup.value = data;
    name.value = deepCloneObject(data.name);
    
    await getTariffServiceGroups();
  }

  isEditing.value = false;
  emit('success', data);
};

watch(isOpened, async () => {
  if (!isOpened.value) {
    return;
  }

  await getServiceGroup();
});
</script>

<style scoped lang="scss">
.services-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  
  margin-top: 16px;
}

.service-item {
  display: flex;
  align-items: center;
  gap: 4px;
}

.chip-item {
  width: fit-content;
}
</style>
